/**
 * PublicTournamentPage.js — Public-facing tournament page with live bracket
 * Route: /t/:joinSlug
 * CSS prefix: ptp-
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicTournamentInfo, getPublicTournamentBracket } from '../services/api';
import Logo from './Logo';
import './PublicTournamentPage.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const SPORT_EMOJI = {
  soccer: '⚽', basketball: '🏀', volleyball: '🏐', softball: '⚾',
  hockey: '🏒', football: '🏈', general: '🏆', winloss: '🏆',
  pool: '🎱', darts: '🎯'
};

const FORMAT_LABELS = {
  single_elim: 'Single Elimination',
  double_elim: 'Double Elimination',
  round_robin: 'Round Robin',
  group_knockout: 'Group Stage + Knockout'
};

const STATUS_CONFIG = {
  draft:               { label: 'Draft',               cls: 'ptp-status-draft' },
  registration_open:   { label: 'Registration Open',   cls: 'ptp-status-reg-open' },
  registration_closed: { label: 'Registration Closed', cls: 'ptp-status-reg-closed' },
  in_progress:         { label: 'Live',                cls: 'ptp-status-live' },
  completed:           { label: 'Completed',           cls: 'ptp-status-completed' },
};

// Bracket layout constants
const MATCH_W  = 200;
const MATCH_H  = 76;
const COL_GAP  = 44;
const SLOT_H   = 96;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateRange(start, end) {
  if (!start) return null;
  if (!end || start === end) return fmtDate(start);
  const s = new Date(start);
  const e = new Date(end);
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function fmtCurrency(cents) {
  if (!cents) return null;
  return `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
}

/**
 * Compute round-robin/group standings from matches data (client-side).
 * pool = null → all matches; pool = 'A' → only pool A matches
 */
function computeStandings(teams, matches, pool = null) {
  const stats = {};
  for (const team of teams) {
    if (pool !== null && team.pool !== pool) continue;
    stats[team.id] = { team, w: 0, d: 0, l: 0, gf: 0, ga: 0, pts: 0 };
  }

  for (const match of matches) {
    if (match.status !== 'completed') continue;
    if (pool !== null && match.pool !== pool) continue;
    if (match.homeScore === null || match.awayScore === null) continue;
    if (!match.homeTeamId || !match.awayTeamId) continue;

    const home = stats[match.homeTeamId];
    const away = stats[match.awayTeamId];
    if (!home || !away) continue;

    home.gf += match.homeScore;
    home.ga += match.awayScore;
    away.gf += match.awayScore;
    away.ga += match.homeScore;

    if (match.homeScore > match.awayScore) {
      home.w++; home.pts += 3; away.l++;
    } else if (match.homeScore < match.awayScore) {
      away.w++; away.pts += 3; home.l++;
    } else {
      home.d++; home.pts += 1;
      away.d++; away.pts += 1;
    }
  }

  return Object.values(stats).sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    const gdA = a.gf - a.ga, gdB = b.gf - b.ga;
    if (gdB !== gdA) return gdB - gdA;
    if (b.gf !== a.gf) return b.gf - a.gf;
    return a.team.teamName.localeCompare(b.team.teamName);
  });
}

// ─── Bracket: Single Elimination ─────────────────────────────────────────────

function matchPos(roundIdx, matchIdx) {
  const x = roundIdx * (MATCH_W + COL_GAP);
  const centerY = (matchIdx + 0.5) * Math.pow(2, roundIdx) * SLOT_H;
  return { x, y: centerY - MATCH_H / 2, centerY };
}

function MatchSlot({ team, score, isWinner, isBye }) {
  if (isBye) return <div className="ptp-slot ptp-slot-bye">BYE</div>;
  return (
    <div className={`ptp-slot${isWinner ? ' ptp-slot-winner' : ''}`}>
      {team?.seed != null && <span className="ptp-slot-seed">#{team.seed}</span>}
      <span className="ptp-slot-name">{team?.teamName || 'TBD'}</span>
      {score !== null && score !== undefined && (
        <span className={`ptp-slot-score${isWinner ? ' ptp-slot-score-win' : ''}`}>{score}</span>
      )}
    </div>
  );
}

function MatchCard({ match }) {
  const status = match.status;
  return (
    <div className={`ptp-match-card ptp-match-${status}`}>
      <MatchSlot
        team={match.homeTeam}
        score={match.homeScore}
        isWinner={match.winnerTeamId != null && match.winnerTeamId === match.homeTeamId}
        isBye={!match.homeTeamId}
      />
      <div className="ptp-match-divider" />
      <MatchSlot
        team={match.awayTeam}
        score={match.awayScore}
        isWinner={match.winnerTeamId != null && match.winnerTeamId === match.awayTeamId}
        isBye={!match.awayTeamId}
      />
    </div>
  );
}

function SingleElimBracket({ rounds }) {
  const numRound1 = rounds[0]?.matches.length ?? 0;
  if (numRound1 === 0) return <p className="ptp-empty">No matches yet.</p>;

  const totalH = Math.max(numRound1 * SLOT_H, MATCH_H + 24);
  const totalW = rounds.length * (MATCH_W + COL_GAP) - COL_GAP;

  const connectors = [];
  rounds.forEach((round, rIdx) => {
    if (rIdx === rounds.length - 1) return;
    round.matches.forEach((match, mIdx) => {
      const { centerY: mCenterY } = matchPos(rIdx, mIdx);
      const matchRightX = rIdx * (MATCH_W + COL_GAP) + MATCH_W;
      const junctionX = matchRightX + COL_GAP / 2;
      const parentIdx = Math.floor(mIdx / 2);
      const { centerY: parentCenterY, x: parentX } = matchPos(rIdx + 1, parentIdx);
      connectors.push(
        <path
          key={`c${rIdx}-${mIdx}`}
          d={`M ${matchRightX} ${mCenterY} H ${junctionX} V ${parentCenterY} H ${parentX}`}
          fill="none"
          stroke="var(--ptp-connector)"
          strokeWidth="2"
        />
      );
    });
  });

  return (
    <div className="ptp-bracket-scroll">
      <div style={{ position: 'relative', width: totalW, height: totalH + 32, marginTop: 28 }}>
        {/* Round labels */}
        {rounds.map((round, rIdx) => (
          <div
            key={round.roundNumber}
            className="ptp-round-label"
            style={{ left: rIdx * (MATCH_W + COL_GAP), width: MATCH_W }}
          >
            {round.roundName}
          </div>
        ))}

        {/* SVG connectors */}
        <svg
          style={{ position: 'absolute', top: 28, left: 0, width: totalW, height: totalH, overflow: 'visible', pointerEvents: 'none' }}
        >
          {connectors}
        </svg>

        {/* Match cards */}
        {rounds.map((round, rIdx) =>
          round.matches.map((match, mIdx) => {
            const { x, y } = matchPos(rIdx, mIdx);
            return (
              <div
                key={match.id}
                style={{ position: 'absolute', left: x, top: y + 28, width: MATCH_W, height: MATCH_H }}
              >
                <MatchCard match={match} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Bracket: Round Robin ─────────────────────────────────────────────────────

function StandingsTable({ rows, showPool }) {
  if (!rows.length) return <p className="ptp-empty">No standings yet.</p>;
  return (
    <div className="ptp-standings-wrap">
      <table className="ptp-standings-table">
        <thead>
          <tr>
            <th>#</th>
            {showPool && <th>Pool</th>}
            <th className="ptp-th-team">Team</th>
            <th>W</th><th>D</th><th>L</th>
            <th>GF</th><th>GA</th><th>GD</th><th>Pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.team.id} className={i % 2 === 0 ? '' : 'ptp-row-alt'}>
              <td className="ptp-td-rank">{i + 1}</td>
              {showPool && <td className="ptp-td-pool">Pool {row.team.pool}</td>}
              <td className="ptp-td-team">{row.team.teamName}</td>
              <td>{row.w}</td><td>{row.d}</td><td>{row.l}</td>
              <td>{row.gf}</td><td>{row.ga}</td>
              <td>{row.gf - row.ga >= 0 ? `+${row.gf - row.ga}` : row.gf - row.ga}</td>
              <td className="ptp-td-pts">{row.pts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundRobinView({ teams, matches }) {
  const standings = useMemo(() => computeStandings(teams, matches), [teams, matches]);

  const rounds = useMemo(() => {
    const byRound = {};
    for (const m of matches) {
      if (!m.awayTeamId) continue; // skip byes
      if (!byRound[m.roundNumber]) byRound[m.roundNumber] = [];
      byRound[m.roundNumber].push(m);
    }
    return Object.entries(byRound)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([num, ms]) => ({ roundNumber: Number(num), roundName: ms[0].roundName || `Round ${num}`, matches: ms }));
  }, [matches]);

  return (
    <div className="ptp-rr-view">
      <h3 className="ptp-section-title">Standings</h3>
      <StandingsTable rows={standings} showPool={false} />

      {rounds.length > 0 && (
        <>
          <h3 className="ptp-section-title ptp-mt">Results</h3>
          {rounds.map(round => (
            <div key={round.roundNumber} className="ptp-rr-round">
              <div className="ptp-rr-round-label">{round.roundName}</div>
              <div className="ptp-rr-matches">
                {round.matches.map(m => (
                  <div key={m.id} className={`ptp-rr-match ptp-match-${m.status}`}>
                    <span className={`ptp-rr-team${m.winnerTeamId === m.homeTeamId ? ' ptp-rr-winner' : ''}`}>
                      {m.homeTeam?.teamName || 'TBD'}
                    </span>
                    <span className="ptp-rr-score">
                      {m.status === 'completed'
                        ? `${m.homeScore} – ${m.awayScore}`
                        : 'vs'}
                    </span>
                    <span className={`ptp-rr-team ptp-rr-right${m.winnerTeamId === m.awayTeamId ? ' ptp-rr-winner' : ''}`}>
                      {m.awayTeam?.teamName || 'TBD'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── Bracket: Group Knockout ──────────────────────────────────────────────────

function GroupKnockoutView({ teams, matches }) {
  const pools = useMemo(() => {
    const set = new Set();
    for (const t of teams) if (t.pool) set.add(t.pool);
    return [...set].sort();
  }, [teams]);

  const [activePool, setActivePool] = useState(pools[0] || null);

  const groupMatches = useMemo(() =>
    matches.filter(m => m.pool != null),
    [matches]
  );
  const knockoutMatches = useMemo(() =>
    matches.filter(m => m.pool === null && m.phase !== null),
    [matches]
  );

  // Build knockout rounds for the elim bracket sub-view
  const knockoutRounds = useMemo(() => {
    if (!knockoutMatches.length) return [];
    const byRound = {};
    for (const m of knockoutMatches) {
      if (!byRound[m.roundNumber]) byRound[m.roundNumber] = [];
      byRound[m.roundNumber].push(m);
    }
    return Object.entries(byRound)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([num, ms]) => ({
        roundNumber: Number(num),
        roundName: ms[0].roundName || ms[0].phase || `Round ${num}`,
        matches: ms.sort((a, b) => a.matchNumber - b.matchNumber)
      }));
  }, [knockoutMatches]);

  const poolStandings = useMemo(() => {
    const result = {};
    for (const pool of pools) {
      const poolTeams = teams.filter(t => t.pool === pool);
      result[pool] = computeStandings(poolTeams, groupMatches, pool);
    }
    return result;
  }, [pools, teams, groupMatches]);

  const poolRounds = useMemo(() => {
    if (!activePool) return [];
    const poolMs = groupMatches.filter(m => m.pool === activePool && m.awayTeamId);
    const byRound = {};
    for (const m of poolMs) {
      if (!byRound[m.roundNumber]) byRound[m.roundNumber] = [];
      byRound[m.roundNumber].push(m);
    }
    return Object.entries(byRound)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([num, ms]) => ({
        roundNumber: Number(num),
        roundName: ms[0].roundName || `Round ${num}`,
        matches: ms
      }));
  }, [activePool, groupMatches]);

  return (
    <div className="ptp-gk-view">
      {/* Pool tabs */}
      {pools.length > 0 && (
        <div className="ptp-pool-tabs">
          {pools.map(p => (
            <button
              key={p}
              className={`ptp-pool-tab${activePool === p ? ' ptp-pool-tab-active' : ''}`}
              onClick={() => setActivePool(p)}
            >
              Pool {p}
            </button>
          ))}
        </div>
      )}

      {/* Selected pool: standings + matches */}
      {activePool && (
        <div className="ptp-pool-content">
          <h3 className="ptp-section-title">Pool {activePool} Standings</h3>
          <StandingsTable rows={poolStandings[activePool] || []} showPool={false} />

          {poolRounds.length > 0 && (
            <>
              <h3 className="ptp-section-title ptp-mt">Pool {activePool} Matches</h3>
              {poolRounds.map(round => (
                <div key={round.roundNumber} className="ptp-rr-round">
                  <div className="ptp-rr-round-label">{round.roundName}</div>
                  <div className="ptp-rr-matches">
                    {round.matches.map(m => (
                      <div key={m.id} className={`ptp-rr-match ptp-match-${m.status}`}>
                        <span className={`ptp-rr-team${m.winnerTeamId === m.homeTeamId ? ' ptp-rr-winner' : ''}`}>
                          {m.homeTeam?.teamName || 'TBD'}
                        </span>
                        <span className="ptp-rr-score">
                          {m.status === 'completed' ? `${m.homeScore} – ${m.awayScore}` : 'vs'}
                        </span>
                        <span className={`ptp-rr-team ptp-rr-right${m.winnerTeamId === m.awayTeamId ? ' ptp-rr-winner' : ''}`}>
                          {m.awayTeam?.teamName || 'TBD'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Knockout bracket */}
      {knockoutRounds.length > 0 && (
        <div className="ptp-knockout-section">
          <h3 className="ptp-section-title ptp-mt">Knockout Bracket</h3>
          <SingleElimBracket rounds={knockoutRounds} />
        </div>
      )}
    </div>
  );
}

// ─── Simple match list (fallback / double elim) ───────────────────────────────

function SimpleMatchList({ matches }) {
  const byRound = useMemo(() => {
    const result = {};
    for (const m of matches) {
      const key = m.roundName || m.phase || `Round ${m.roundNumber}`;
      if (!result[key]) result[key] = [];
      result[key].push(m);
    }
    return result;
  }, [matches]);

  return (
    <div className="ptp-rr-view">
      {Object.entries(byRound).map(([round, ms]) => (
        <div key={round} className="ptp-rr-round">
          <div className="ptp-rr-round-label">{round}</div>
          <div className="ptp-rr-matches">
            {ms.map(m => (
              <div key={m.id} className={`ptp-rr-match ptp-match-${m.status}`}>
                <span className={`ptp-rr-team${m.winnerTeamId === m.homeTeamId ? ' ptp-rr-winner' : ''}`}>
                  {m.homeTeam?.teamName || 'TBD'}
                </span>
                <span className="ptp-rr-score">
                  {m.status === 'completed' ? `${m.homeScore} – ${m.awayScore}` : 'vs'}
                </span>
                <span className={`ptp-rr-team ptp-rr-right${m.winnerTeamId === m.awayTeamId ? ' ptp-rr-winner' : ''}`}>
                  {m.awayTeam?.teamName || 'TBD'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── BracketView router ───────────────────────────────────────────────────────

function BracketView({ formatType, teams, matches }) {
  if (!matches.length) {
    return (
      <div className="ptp-empty-bracket">
        <p>The bracket has not been generated yet.</p>
        <p className="ptp-empty-sub">Check back once registration closes and the bracket is set.</p>
      </div>
    );
  }

  if (formatType === 'round_robin') {
    return <RoundRobinView teams={teams} matches={matches} />;
  }

  if (formatType === 'group_knockout') {
    return <GroupKnockoutView teams={teams} matches={matches} />;
  }

  if (formatType === 'single_elim') {
    const rounds = (() => {
      const byRound = {};
      for (const m of matches) {
        if (!byRound[m.roundNumber]) byRound[m.roundNumber] = [];
        byRound[m.roundNumber].push(m);
      }
      return Object.entries(byRound)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([num, ms]) => ({
          roundNumber: Number(num),
          roundName: ms[0].roundName || `Round ${num}`,
          matches: ms.sort((a, b) => a.matchNumber - b.matchNumber)
        }));
    })();
    return <SingleElimBracket rounds={rounds} />;
  }

  // double_elim or unknown: fall back to list view
  return <SimpleMatchList matches={matches} />;
}

// ─── Teams Grid ───────────────────────────────────────────────────────────────

function TeamStatusBadge({ status }) {
  const map = {
    confirmed: { label: 'Confirmed', cls: 'ptp-team-confirmed' },
    waitlisted: { label: 'Waitlisted', cls: 'ptp-team-waitlisted' },
    eliminated: { label: 'Eliminated', cls: 'ptp-team-eliminated' },
    champion: { label: 'Champion 🏆', cls: 'ptp-team-champion' },
  };
  const cfg = map[status] || { label: status, cls: '' };
  return <span className={`ptp-team-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function TeamsGrid({ teams }) {
  if (!teams.length) {
    return <p className="ptp-empty">No teams registered yet.</p>;
  }
  const sorted = [...teams].sort((a, b) => {
    if (a.seed != null && b.seed != null) return a.seed - b.seed;
    if (a.seed != null) return -1;
    if (b.seed != null) return 1;
    return a.teamName.localeCompare(b.teamName);
  });
  return (
    <div className="ptp-teams-grid">
      {sorted.map((team, i) => (
        <div key={team.id} className="ptp-team-card">
          <div className="ptp-team-number">
            {team.seed != null ? `#${team.seed}` : i + 1}
          </div>
          <div className="ptp-team-info">
            <div className="ptp-team-name">{team.teamName}</div>
            {team.pool && <div className="ptp-team-pool">Pool {team.pool}</div>}
          </div>
          <TeamStatusBadge status={team.status} />
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PublicTournamentPage() {
  const { joinSlug } = useParams();
  const [info, setInfo] = useState(null);
  const [bracketData, setBracketData] = useState(null);
  const [tab, setTab] = useState('bracket');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [infoRes, bracketRes] = await Promise.all([
        getPublicTournamentInfo(joinSlug),
        getPublicTournamentBracket(joinSlug),
      ]);
      setInfo(infoRes);
      setBracketData(bracketRes);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [joinSlug]);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Auto-refresh when live
  useEffect(() => {
    const status = bracketData?.tournament?.status;
    if (status !== 'in_progress') return;
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [bracketData?.tournament?.status, load]);

  if (loading) {
    return (
      <div className="ptp-page">
        <div className="ptp-loading">Loading tournament...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ptp-page">
        <div className="ptp-error">
          <h2>Tournament not found</h2>
          <p>{error}</p>
          <Link to="/" className="ptp-back-link">← Go Home</Link>
        </div>
      </div>
    );
  }

  const tournament = bracketData?.tournament || info?.tournament;
  const teams = bracketData?.teams || [];
  const matches = bracketData?.matches || [];
  const infoTournament = info?.tournament;

  const statusCfg = STATUS_CONFIG[tournament?.status] || { label: tournament?.status, cls: '' };
  const isLive = tournament?.status === 'in_progress';
  const champion = teams.find(t => t.status === 'champion');

  return (
    <div className="ptp-page">
      {/* Branding Bar */}
      <div className="ptp-branding-bar">
        <a href="https://leaguemaker.app" className="ptp-branding-logo" target="_blank" rel="noopener noreferrer">
          <Logo size="1.2rem" variant="white" />
        </a>
        <a href="https://leaguemaker.app/signup" className="ptp-branding-cta" target="_blank" rel="noopener noreferrer">
          Start a League — Free
        </a>
      </div>

      {/* Header */}
      <div className="ptp-header">
        <div className="ptp-header-inner">
          <div className="ptp-header-top">
            <span className="ptp-sport-icon">
              {SPORT_EMOJI[tournament?.sport] || '🏆'}
            </span>
            <div className="ptp-header-text">
              <h1 className="ptp-title">{tournament?.name}</h1>
              <div className="ptp-meta">
                <span className={`ptp-status-badge ${statusCfg.cls}`}>
                  {isLive && <span className="ptp-live-dot" />}
                  {statusCfg.label}
                </span>
                {FORMAT_LABELS[tournament?.formatType] && (
                  <span className="ptp-meta-item">{FORMAT_LABELS[tournament?.formatType]}</span>
                )}
                {(tournament?.startDate || tournament?.endDate) && (
                  <span className="ptp-meta-item">
                    {fmtDateRange(tournament.startDate, tournament.endDate)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Champion banner */}
          {champion && (
            <div className="ptp-champion-banner">
              🏆 Champion: <strong>{champion.teamName}</strong>
            </div>
          )}

          {/* Registration CTA */}
          {info?.registrationOpen && (
            <div className="ptp-reg-cta">
              <Link to={`/join/t/${joinSlug}`} className="ptp-reg-btn">
                Register Now
                {info.spotsRemaining != null && (
                  <span className="ptp-spots"> · {info.spotsRemaining} spot{info.spotsRemaining !== 1 ? 's' : ''} left</span>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="ptp-tabs">
        <button
          className={`ptp-tab${tab === 'bracket' ? ' ptp-tab-active' : ''}`}
          onClick={() => setTab('bracket')}
        >
          {tournament?.formatType === 'round_robin' ? 'Standings & Results' : 'Bracket'}
        </button>
        <button
          className={`ptp-tab${tab === 'teams' ? ' ptp-tab-active' : ''}`}
          onClick={() => setTab('teams')}
        >
          Teams
          {teams.length > 0 && <span className="ptp-tab-count">{teams.length}</span>}
        </button>
        <button
          className={`ptp-tab${tab === 'info' ? ' ptp-tab-active' : ''}`}
          onClick={() => setTab('info')}
        >
          Info
        </button>
      </div>

      {/* Content */}
      <div className="ptp-content">

        {/* Bracket / Results tab */}
        {tab === 'bracket' && (
          <div className="ptp-bracket-tab">
            {isLive && lastUpdated && (
              <div className="ptp-live-indicator">
                <span className="ptp-live-pulse" />
                Live · updates every 30s · last updated {lastUpdated.toLocaleTimeString()}
              </div>
            )}
            <BracketView
              formatType={tournament?.formatType}
              teams={teams}
              matches={matches}
            />
          </div>
        )}

        {/* Teams tab */}
        {tab === 'teams' && (
          <TeamsGrid teams={teams} />
        )}

        {/* Info tab */}
        {tab === 'info' && (
          <div className="ptp-info-tab">
            {infoTournament?.description && (
              <div className="ptp-info-block">
                <h3 className="ptp-info-label">About</h3>
                <p className="ptp-info-value">{infoTournament.description}</p>
              </div>
            )}
            <div className="ptp-info-grid">
              {infoTournament?.sport && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Sport</div>
                  <div className="ptp-info-value">
                    {SPORT_EMOJI[infoTournament.sport]} {infoTournament.sport.charAt(0).toUpperCase() + infoTournament.sport.slice(1)}
                  </div>
                </div>
              )}
              {FORMAT_LABELS[infoTournament?.formatType] && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Format</div>
                  <div className="ptp-info-value">{FORMAT_LABELS[infoTournament.formatType]}</div>
                </div>
              )}
              {infoTournament?.startDate && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Dates</div>
                  <div className="ptp-info-value">
                    {fmtDateRange(infoTournament.startDate, infoTournament.endDate)}
                  </div>
                </div>
              )}
              {infoTournament?.registrationDeadline && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Registration Deadline</div>
                  <div className="ptp-info-value">{fmtDate(infoTournament.registrationDeadline)}</div>
                </div>
              )}
              {infoTournament?.maxTeams && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Teams</div>
                  <div className="ptp-info-value">
                    {info?.teamCount ?? teams.length} / {infoTournament.maxTeams}
                  </div>
                </div>
              )}
              {infoTournament?.entryFee != null && infoTournament.entryFee > 0 && (
                <div className="ptp-info-block">
                  <div className="ptp-info-label">Entry Fee</div>
                  <div className="ptp-info-value">{fmtCurrency(infoTournament.entryFee)}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="ptp-footer">
        <span>Powered by </span>
        <a href="/" className="ptp-footer-link">LeagueMaker</a>
      </div>
    </div>
  );
}
